import * as React from 'react';

import { domToReact, HTMLReactParserOptions, htmlToDOM } from 'html-react-parser';
import { Carousel } from 'react-responsive-carousel';
import MultiCarousel from '../MultiCarousel';
import classNames from 'classnames';

import './styles.scss';
import { includes } from 'lodash';
import Icon from '../Icon';

export interface IRenderCarouselProps {
    customerId?: number;
    /* DOM to parse */
    dom: string | object[];
    /** Unsafe config */
    config?: any;
    /** Custom component mapper */
    componentMapper?: (maybeComponent: string, maybeContent: string, maybeConfig) => React.Component;
    /** Replace config */
    replace?: HTMLReactParserOptions['replace'];
}


/**
 * Renders DOM string to gallery. Designed for HTML hydration.
 */
const RenderCarousel: React.FunctionComponent<IRenderCarouselProps> = (props) => {
    const { customerId } = props;
    const slides: React.ReactElement[] = [];
    const parserSlideOptions: HTMLReactParserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (props.replace) {
                    return props.replace(domNode);
                } else if (domNode.attribs['data-subcomponent'] && props.componentMapper) {
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-subcomponent'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                } else if (domNode.attribs['data-component'] && props.componentMapper) {
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-component'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                } else {
                    const clickHandler = (e) => {
                        const target = e.target.closest('a');
                        if (target) {
                            const gtmContext = target.getAttribute('data-gtm-context');

                            if (gtmContext) {
                                try {
                                    const gtmData = JSON.parse(target.getAttribute('data-gtm-context'));

                                    window.dispatchEvent(
                                        new CustomEvent('promotional-slide-click', {
                                            detail: {
                                                creativeName: gtmData.creativeName,
                                                creativeSlot: gtmData.creativeSlot,
                                                locationId: gtmData.locationId,
                                                userId: customerId,
                                            },
                                        })
                                    );
                                } catch (e) {
                                    // No gtm data found
                                }
                            }
                        }
                    };

                    const { class: className, ...rest } = domNode.attribs;
                    Object.assign(rest, { onClick: clickHandler });

                    return (
                        <domNode.name style={props.config?.height && { flexBasis: props.config?.height }} className={classNames(className, props.config?.height && 'has-custom-height')} {...rest}>
                            {domToReact(domNode.children, {})}
                        </domNode.name>
                    );
                }
            }
            return false;
        },
    };

    const parserOptions: HTMLReactParserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'div') {
                    const { class: className, ...rest } = domNode.attribs;
                    if (includes(className, 'carousel-items')) {
                        [...domToReact(domNode.children, parserSlideOptions)].map((i) => {
                            slides.push(i);
                        });

                        return (
                            <React.Fragment/>
                        );
                    }
                    return (
                        <React.Fragment/>
                    );
                }
            } else {
                return (
                    <React.Fragment/>
                );
            }
            return false;
        },
    };

    // Main output

    const domTree = Array.isArray(props.dom) ? props.dom : htmlToDOM(props.dom.replace(/>\s+</g, '><'));
    domToReact(domTree, parserOptions);

    return (
        <React.Fragment>
            <div className={classNames('carousel', 'carousel-inline', {
                'has-arrows': !!props.config?.showArrows,
                'size-full': !!props.config?.fullWidth,
                'size-auto': !props.config?.fullWidth,
                'type-multiple': !!props.config?.multiple,
                'type-single': !props.config?.multiple,
                'mobile-scale': props.config?.cropMobile === false,
            })}>
                {!!props.config?.multiple ? (
                    <MultiCarousel
                        showDots={!!props.config?.showIndicators || false}
                        showArrows={!!props.config?.showArrows || false}
                        hideControls={props.config?.hideControls}
                        ariaConfig={props.config?.ariaConfig}
                        renderArrowsOutside={props.config?.renderArrowsOutside !== null ? props.config?.renderArrowsOutside : true}
                        type={props.config?.type}
                        autoPlay={props.config?.autoPlay}
                        autoPlaySpeed={props.config?.autoPlaySpeed}
                        transitionSpeed={props.config?.transitionSpeed}
                        responsive={props.config?.responsive}
                        showNumbers={props.config?.showNumbers}
                    >
                        {slides}
                    </MultiCarousel>
                ) : (
                    <Carousel
                        showStatus={false}
                        showThumbs={false}
                        swipeable={true}
                        infiniteLoop={props.config?.autoPlay || props.config?.autoplay || false}
                        showArrows={!!props.config?.showArrows && slides.length > 1}
                        showIndicators={!!props.config?.showIndicators || false}
                        autoPlay={props.config?.autoPlay || props.config?.autoplay || false}
                        interval={props.config?.autoplaySpeed || 3000}
                        emulateTouch={true}
                        useKeyboardArrows={true}
                        renderArrowNext={(!!props.config?.showArrows && slides.length > 1) ? (onClick) => {
                            return (
                                <button onClick={onClick} className="control-arrow control-next"><Icon kind="arrow5-right" width={20} height={20}/></button>
                            );
                        } : () => {
                            return (
                                <React.Fragment/>
                            );
                        }}
                        renderArrowPrev={(!!props.config?.showArrows && slides.length > 1) ? (onClick) => {
                            return (
                                <button onClick={onClick} className="control-arrow control-prev"><Icon kind="arrow5-left" width={20} height={20}/></button>
                            );
                        } : () => {
                            return (
                                <React.Fragment/>
                            );
                        }}
                    >
                        {slides}
                    </Carousel>
                )}

            </div>
        </React.Fragment>
    );
};

RenderCarousel.displayName = 'RenderCarousel';

export default RenderCarousel;
